import type { ComponentProps, FC, ReactNode } from 'react';

import { type InputBaseComponentProps, TextField, type TextFieldProps } from '@mui/material';
import { clsx } from 'clsx';
import { type RegisterOptions, useController, useFormContext } from 'react-hook-form';
import InputMask from 'react-input-mask';
import type { ZodType } from 'zod';

import { useDebouncedFieldValue } from 'shared/lib/forms/useDebouncedFieldValue';
import { useMergedRules } from 'shared/lib/forms/useMergedRules';

import { Field } from './Field';
import { RecommendedLengthHint } from './RecommendedLengthHint';

// MUI defaults the input placeholder to a low-opacity colored variant; we want the standard
// grey-60 token used everywhere else in the dashboard, so override the placeholder color and
// reset the opacity here for every TextFormField.
const PLACEHOLDER_CLASS =
  '[&_input::placeholder]:text-grey60 [&_input::placeholder]:opacity-100 [&_textarea::placeholder]:text-grey60 [&_textarea::placeholder]:opacity-100';

type FieldExtraProps = Pick<ComponentProps<typeof Field>, 'orientation' | 'indent' | 'labelAction'>;

type TextFormFieldProps = Omit<
  TextFieldProps,
  'name' | 'error' | 'helperText' | 'value' | 'defaultValue' | 'inputRef'
> &
  FieldExtraProps & {
    name: string;
    label?: ReactNode;
    description?: ReactNode;
    rules?: RegisterOptions;
    /** Validated inline, alongside `rules`. Keep the reference stable — build it outside render. */
    schema?: ZodType;
    /** Marks the label; the rule that enforces it still belongs in `schema` or `rules` */
    required?: boolean;
    /** An advisory note about an otherwise valid value; an error takes its place while there is one */
    warning?: ReactNode;
    /**
     * Holds keystrokes back from the form until typing settles, so watchers can drive a lookup
     * without a request per character. Blur and Enter write a pending keystroke through at once.
     */
    debounceMs?: number;
    maxLength?: number;
    /** `react-input-mask` pattern, where 9 is a digit - the mask writes the separators itself */
    mask?: string;
    // Soft cap; values above this show an amber helper text but do not block submit.
    recommendedLength?: number;
    'data-testid'?: string;
  };

export const TextFormField: FC<TextFormFieldProps> = ({
  name,
  label,
  description,
  rules,
  schema,
  required,
  warning,
  orientation,
  indent,
  labelAction,
  onChange,
  onBlur,
  onKeyDown,
  debounceMs,
  maxLength,
  mask,
  recommendedLength,
  'data-testid': dataTestId,
  ...textFieldProps
}) => {
  const { control } = useFormContext();
  const mergedRules = useMergedRules({ rules, maxLength, schema });

  const {
    field,
    fieldState: { error },
  } = useController({ control, name, rules: mergedRules });

  const { value, isPending, onChange: commitValue, flush } = useDebouncedFieldValue({ field, delayMs: debounceMs });

  // A verdict reached on the committed value stops describing the field while a keystroke is
  // still on its way to the form
  const settledError = isPending ? undefined : error;

  const handleBlur: TextFieldProps['onBlur'] = (event) => {
    flush();
    field.onBlur();
    onBlur?.(event);
  };

  const handleChange: TextFieldProps['onChange'] = (event) => {
    commitValue(event.target.value);
    onChange?.(event);
  };

  const renderTextField = (inputBindings: Pick<TextFieldProps, 'value' | 'onBlur' | 'onChange' | 'inputProps'>) => (
    <TextField
      fullWidth
      {...textFieldProps}
      {...inputBindings}
      className={clsx(PLACEHOLDER_CLASS, textFieldProps.className)}
      data-testid={dataTestId ?? name}
      error={!!settledError}
      id={name}
      inputRef={field.ref}
      name={field.name}
      onKeyDown={(event) => {
        // A native submit reads the form the moment this bubbles, so a pending keystroke has to land first
        if (event.key === 'Enter') {
          flush();
        }

        onKeyDown?.(event);
      }}
    />
  );

  return (
    <Field
      description={description}
      error={settledError?.message}
      helperText={
        <RecommendedLengthHint length={value.length} maxLength={maxLength} recommendedLength={recommendedLength} />
      }
      htmlFor={name}
      indent={indent}
      label={label}
      required={required}
      warning={isPending ? undefined : warning}
      labelAction={labelAction}
      orientation={orientation}
    >
      {mask ? (
        // react-input-mask owns value and the change/blur handlers, and hands them to the input it renders
        <InputMask
          alwaysShowMask={false}
          disabled={textFieldProps.disabled}
          mask={mask}
          value={value}
          onBlur={handleBlur}
          onChange={handleChange}
        >
          {(maskInputProps: Partial<InputBaseComponentProps>) =>
            renderTextField({ inputProps: { ...maskInputProps, ...textFieldProps.inputProps } })
          }
        </InputMask>
      ) : (
        renderTextField({ value, onBlur: handleBlur, onChange: handleChange })
      )}
    </Field>
  );
};
